import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Lock, Clock, Trophy, BookOpen } from 'lucide-react'

export default function PathCard({
  slug,
  title,
  description,
  icon,
  difficulty,
  estimatedDuration,
  totalChapters,
  completedChapters = 0,
  xpReward,
  locked = false,
  coverImage
}) {
  const progress = totalChapters > 0 ? (completedChapters / totalChapters) * 100 : 0
  const isInProgress = completedChapters > 0 && completedChapters < totalChapters
  const isCompleted = completedChapters === totalChapters

  const difficultyColors = {
    beginner: 'text-green-600 bg-green-50',
    intermediate: 'text-yellow-600 bg-yellow-50',
    advanced: 'text-red-600 bg-red-50'
  }

  const CardContent = () => (
    <motion.div
      whileHover={!locked ? { y: -4, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' } : {}}
      className={`
        relative overflow-hidden rounded-2xl border-2 transition-all
        ${locked
          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
          : 'border-gray-200 bg-white cursor-pointer hover:border-green-500'
        }
      `}
    >
      {/* Cover Image or Icon */}
      <div className="relative h-48 bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
        {coverImage ? (
          <img src={coverImage} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="text-8xl">{icon || '🎯'}</div>
        )}

        {locked && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <Lock className="w-16 h-16 text-white" />
          </div>
        )}

        {/* Progress Badge */}
        {!locked && isInProgress && (
          <div className="absolute top-3 right-3 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            In Progress
          </div>
        )}

        {isCompleted && (
          <div className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
            <Trophy className="w-4 h-4" />
            Completed
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Difficulty Badge */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${difficultyColors[difficulty] || difficultyColors.beginner}`}>
            {difficulty}
          </span>
          {xpReward && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-600 flex items-center gap-1">
              <Trophy className="w-3 h-3" />
              {xpReward} XP
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>

        {/* Description */}
        <p className="text-gray-600 mb-4 line-clamp-3">{description}</p>

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{estimatedDuration} min</span>
          </div>
          <div className="flex items-center gap-1">
            <BookOpen className="w-4 h-4" />
            <span>{totalChapters} chapters</span>
          </div>
        </div>

        {/* Progress Bar */}
        {!locked && progress > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>Progress</span>
              <span>{completedChapters}/{totalChapters} chapters</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
                className="h-full bg-gradient-to-r from-green-500 to-green-600"
              />
            </div>
          </div>
        )}

        {/* CTA Button */}
        <button
          disabled={locked}
          className={`
            w-full py-3 rounded-lg font-semibold transition-all
            ${locked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : isInProgress
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : isCompleted
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-green-600 text-white hover:bg-green-700'
            }
          `}
        >
          {locked
            ? 'Locked'
            : isInProgress
              ? 'Continue Path'
              : isCompleted
                ? 'Play Again'
                : 'Start Path'
          }
        </button>
      </div>
    </motion.div>
  )

  return locked ? (
    <CardContent />
  ) : (
    <Link to={`/paths/${slug}`}>
      <CardContent />
    </Link>
  )
}
